import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import AdminLayout from '../components/AdminLayout';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  Plus, Pencil, Trash2, X, Check, Loader2, Calendar, Eye, EyeOff
} from 'lucide-react';

import { BACKEND_URL } from '../config/api';

const AdminOccasions = () => {
  const { getAuthHeaders } = useAuth();
  const [occasions, setOccasions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editValue, setEditValue] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [newValue, setNewValue] = useState('');
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchOccasions();
  }, []);

  const fetchOccasions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/occasions`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setOccasions(data.occasions || []);
      }
    } catch (err) {
      setError('Failed to load occasions');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/occasions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          label: newLabel.trim(),
          value: newValue.trim() || undefined
        })
      });
      if (res.ok) {
        setNewLabel('');
        setNewValue('');
        setAdding(false);
        fetchOccasions();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to add occasion');
      }
    } catch {
      setError('Failed to add occasion');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (occasionId) => {
    if (!editLabel.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/occasions/${occasionId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ 
          label: editLabel.trim(),
          value: editValue.trim() || undefined
        })
      });
      if (res.ok) {
        setEditingId(null);
        setEditLabel('');
        setEditValue('');
        fetchOccasions();
      } else {
        const data = await res.json();
        setError(data.detail || 'Failed to update occasion');
      }
    } catch {
      setError('Failed to update occasion');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (occ) => {
    setError(null);
    try {
      await fetch(`${BACKEND_URL}/api/admin/occasions/${occ.occasion_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ is_active: !occ.is_active })
      });
      fetchOccasions();
    } catch {
      setError('Failed to toggle occasion status');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setError(null);
    try {
      await fetch(`${BACKEND_URL}/api/admin/occasions/${deleteTarget.occasion_id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      setDeleteTarget(null);
      fetchOccasions();
    } catch {
      setError('Failed to delete occasion');
    }
  };

  const startEdit = (occ) => {
    setEditingId(occ.occasion_id);
    setEditLabel(occ.label);
    setEditValue(occ.value);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel('');
    setEditValue('');
  };

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Calendar className="text-sage" size={24} />
            <h1 className="text-xl font-medium text-charcoal">Occasions</h1>
            <span className="text-sm text-gray-soft">({occasions.length})</span>
          </div>
          <button
            onClick={() => setAdding(true)}
            className="flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg hover:bg-sage-dark transition-colors text-sm"
            data-testid="add-occasion-btn"
          >
            <Plus size={16} />
            Add Occasion
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Add Form */}
        {adding && (
          <div className="mb-4 p-4 bg-white rounded-lg border border-beige">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <label className="block text-xs text-gray-soft mb-1">Display Label *</label>
                <input
                  type="text"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder="e.g. Mother's Day"
                  className="w-full px-3 py-2 border border-beige rounded-lg focus:border-sage focus:ring-1 focus:ring-sage text-sm"
                  autoFocus
                  data-testid="new-occasion-label"
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs text-gray-soft mb-1">Value (auto-generated if empty)</label>
                <input
                  type="text"
                  value={newValue}
                  onChange={(e) => setNewValue(e.target.value)}
                  placeholder="e.g. mother's day"
                  className="w-full px-3 py-2 border border-beige rounded-lg focus:border-sage focus:ring-1 focus:ring-sage text-sm"
                  data-testid="new-occasion-value"
                />
              </div>
              <div className="flex items-end gap-2">
                <button
                  onClick={handleAdd}
                  disabled={saving || !newLabel.trim()}
                  className="p-2 bg-sage text-white rounded-lg hover:bg-sage-dark disabled:opacity-50 transition-colors"
                  data-testid="save-new-occasion"
                >
                  {saving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                </button>
                <button
                  onClick={() => { setAdding(false); setNewLabel(''); setNewValue(''); }}
                  className="p-2 text-gray-soft hover:text-charcoal border border-beige rounded-lg hover:bg-cream transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="animate-spin text-sage" size={32} />
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-beige overflow-hidden">
            <table className="w-full">
              <thead className="bg-cream/50">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-soft uppercase">Label</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-gray-soft uppercase">Value</th>
                  <th className="text-center px-4 py-3 text-xs font-medium text-gray-soft uppercase">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-gray-soft uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-beige">
                {occasions.map((occ) => (
                  <tr key={occ.occasion_id} className={!occ.is_active ? 'bg-gray-50' : ''}>
                    <td className="px-4 py-3">
                      {editingId === occ.occasion_id ? (
                        <input
                          type="text"
                          value={editLabel}
                          onChange={(e) => setEditLabel(e.target.value)}
                          className="w-full px-2 py-1 border border-sage rounded text-sm"
                          autoFocus
                        />
                      ) : (
                        <span className={`text-sm ${occ.is_active ? 'text-charcoal' : 'text-gray-soft'}`}>
                          {occ.label}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {editingId === occ.occasion_id ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full px-2 py-1 border border-sage rounded text-sm"
                        />
                      ) : (
                        <code className={`text-xs px-2 py-0.5 rounded ${occ.is_active ? 'bg-cream text-charcoal' : 'bg-gray-100 text-gray-soft'}`}>
                          {occ.value}
                        </code>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleToggleActive(occ)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                          occ.is_active
                            ? 'bg-sage/10 text-sage hover:bg-sage/20'
                            : 'bg-gray-100 text-gray-soft hover:bg-gray-200'
                        }`}
                        title={occ.is_active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {occ.is_active ? <Eye size={12} /> : <EyeOff size={12} />}
                        {occ.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {editingId === occ.occasion_id ? (
                          <>
                            <button
                              onClick={() => handleUpdate(occ.occasion_id)}
                              disabled={saving}
                              className="p-1.5 text-sage hover:bg-sage/10 rounded transition-colors"
                            >
                              {saving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                            </button>
                            <button
                              onClick={cancelEdit}
                              className="p-1.5 text-gray-soft hover:bg-cream rounded transition-colors"
                            >
                              <X size={16} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => startEdit(occ)}
                              className="p-1.5 text-gray-soft hover:text-charcoal hover:bg-cream rounded transition-colors"
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => setDeleteTarget(occ)}
                              className="p-1.5 text-gray-soft hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          title="Delete Occasion"
          message={`Are you sure you want to delete "${deleteTarget?.label}"? This cannot be undone.`}
          confirmText="Delete"
          confirmVariant="danger"
        />
      </div>
    </AdminLayout>
  );
};

export default AdminOccasions;
